import { SyncStatusMap } from "./visualizations/visualizations";

const _DBName = 'localDB';
const _Version = 4;

const openDatabase = async () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(_DBName, _Version);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;

            // helper function to safely create a store
            const createStoreIfNotExists = (name, options) => {
                if (!db.objectStoreNames.contains(name)) {
                    db.createObjectStore(name, options);
                }
            };

            // Create object stores if they don’t exist yet
            createStoreIfNotExists('customer', { keyPath: 'ID' });
            createStoreIfNotExists('item', { keyPath: 'ID' });
            createStoreIfNotExists('holiday', { keyPath: 'ID' });
            createStoreIfNotExists('session', { keyPath: 'ID' });
            createStoreIfNotExists('sessionNotes', { keyPath: 'ID' });
            createStoreIfNotExists('sessionItems', { keyPath: 'ID' });
            createStoreIfNotExists('file', { keyPath: 'ID' });
            createStoreIfNotExists('institution', { keyPath: 'ID' });
            createStoreIfNotExists('territories', { keyPath: 'ID' });
            createStoreIfNotExists('unit', { keyPath: 'ID' });
            createStoreIfNotExists('Cycle', { keyPath: 'ID' });
            createStoreIfNotExists('RecipientPhotoFile', { keyPath: 'ID' });
            createStoreIfNotExists('RecipientSignatureFile', { keyPath: 'ID' });
            createStoreIfNotExists('MissedCallReasons', { keyPath: 'ID' });
            createStoreIfNotExists('AdvancedCallReasons', { keyPath: 'ID' });
            createStoreIfNotExists('Attendance', { keyPath: 'ID' });
            createStoreIfNotExists('SessionInventory', { keyPath: 'ID' });
            createStoreIfNotExists('SessionWithdrawal', { keyPath: 'ID' });
            createStoreIfNotExists('InvImages', { keyPath: 'ID' });
            createStoreIfNotExists('SessionForRequest', { keyPath: 'ID' });
            createStoreIfNotExists('Announcements', { keyPath: 'ID' });
            createStoreIfNotExists('SessionEvaluations', { keyPath: 'ID' });
            createStoreIfNotExists('EvaluationTemplates', { keyPath: 'ID' });
            createStoreIfNotExists('Client', { keyPath: 'ID' });
            createStoreIfNotExists('Logs', { keyPath: 'ID' });
            createStoreIfNotExists('Worksheets', { keyPath: 'ID' });
            createStoreIfNotExists('PlanningAccounts', { keyPath: 'ID' });
            createStoreIfNotExists('Activities', { keyPath: 'ID' });
            createStoreIfNotExists('LeaveTypes', { keyPath: 'ID' });
            createStoreIfNotExists('Events', { keyPath: 'ID' });
            createStoreIfNotExists('CycleEvents', { keyPath: 'ID' });
            createStoreIfNotExists('Policies', { keyPath: 'ID' });
            createStoreIfNotExists('SessionJointDays', { keyPath: 'ID' });
            createStoreIfNotExists('Orders', { keyPath: 'ID' });
            createStoreIfNotExists('OrderDetails', { keyPath: 'ID' });
            createStoreIfNotExists('StockAllocations', { keyPath: 'ID' });
        };

        request.onsuccess = (event) => {
            resolve(event.target.result);
        };

        request.onerror = (event) => {
            reject(event.target.error);
        };
    });
};


const syncDataToLocal = async (storeName, newData) => {
    try {
        const db = await openDatabase();

        return new Promise((resolve) => {
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);

            transaction.oncomplete = () => {
                resolve({ success: true, message: 'Data synced successfully.' });
            };

            transaction.onerror = (event) => {
                const error = event.target.error;
                resolve({
                    success: false,
                    message: `[${storeName}] Transaction error: ${error?.message || 'Transaction failed.'}`
                });
            };

            // Upsert items one by one
            try {
                for (const item of newData) {
                    store.put(item); // put() will update if exists, insert if not
                }
            } catch (err) {
                resolve({
                    success: false,
                    message: `[${storeName}] Item processing error: ${err.message || 'Error processing items.'}`
                });
            }
        });

    } catch (err) {
        return {
            success: false,
            message: `[${storeName}] Database open error: ${err.message || 'Failed to open database.'}`
        };
    }
};

const getAll = async (storeName) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(_DBName, _Version);

        request.onsuccess = (event) => {
            const db = event.target.result;

            if (!db.objectStoreNames.contains(storeName)) {
                resolve(null); // Return null if store doesn't exist
                return;
            }

            const transaction = db.transaction(storeName, 'readonly');
            const store = transaction.objectStore(storeName);
            const getAllRequest = store.getAll();

            getAllRequest.onsuccess = () => {
                resolve(getAllRequest.result);
            };

            getAllRequest.onerror = (event) => {
                reject(event.target.error);
            };
        };

        request.onerror = (event) => {
            reject(event.target.error);
        };
    });
};

// Fetch a specific record by its ID
const getRecordById = async (storeName, id) => {
    if (id === undefined || id === null || id === "" || (typeof id === "number" && Number.isNaN(id))) {
        return null; // Return nothing (null) if ID is not valid
    }

    return new Promise((resolve, reject) => {
        const request = indexedDB.open(_DBName, _Version);

        request.onsuccess = (event) => {
            const db = event.target.result;
            const transaction = db.transaction(storeName, 'readonly');
            const store = transaction.objectStore(storeName);
            const getRequest = store.get(id);

            getRequest.onsuccess = () => {
                resolve(getRequest.result);
            };

            getRequest.onerror = (event) => {
                console.log(storeName);
                reject(event.target.error);
            };
        };

        request.onerror = (event) => {
                console.log(storeName);
            reject(event.target.error);
        };
    });
};

// Add a new record to the specified store
const addRecord = async (storeName, data) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(_DBName, _Version);

        request.onsuccess = (event) => {
            const db = event.target.result;
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);
            const addRequest = store.add(data); // Use 'add' to insert a new record

            addRequest.onsuccess = () => {
                resolve(`Record added to ${storeName} store successfully.`);
            };

            addRequest.onerror = (event) => {
                const error = event.target.error;
                if (error.name === 'ConstraintError') {
                    console.warn(`Duplicate key in ${storeName}, record skipped.`);
                    resolve(`Duplicate key ignored for ${storeName}.`);
                } else {
                    reject(error);
                }
            };
        };

        request.onerror = (event) => {
            reject(event.target.error);
        };
    });
};


// Delete a record by its ID
const deleteRecord = async (storeName, id) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(_DBName, _Version);

        request.onsuccess = (event) => {
            const db = event.target.result;
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);
            const deleteRequest = store.delete(id);

            deleteRequest.onsuccess = () => {
                resolve(`Record with ID ${id} deleted successfully.`);
            };

            deleteRequest.onerror = (event) => {
                reject(event.target.error);
            };
        };

        request.onerror = (event) => {
            reject(event.target.error);
        };
    });
};

const updateRecord = async (storeName, data) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(_DBName, _Version);

        request.onsuccess = (event) => {
            const db = event.target.result;
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);
            const putRequest = store.put(data); // Use 'put' to update

            putRequest.onsuccess = () => {
                resolve(`Record with ID ${data.id} updated successfully.`);
            };

            putRequest.onerror = (event) => {
                reject(event.target.error);
            };
        };

        request.onerror = (event) => {
            reject(event.target.error);
        };
    });
};

const getNextId = async (storeName) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(_DBName, _Version);

        request.onsuccess = (event) => {
            const db = event.target.result;
            const transaction = db.transaction(storeName, 'readonly');
            const store = transaction.objectStore(storeName);
            const keyPath = store.keyPath;

            const getAllRequest = store.getAll();

            getAllRequest.onsuccess = () => {
                const items = getAllRequest.result;
                if (!keyPath) {
                    reject(new Error("Key path not found for store: " + storeName));
                    return;
                }

                if (items.length === 0) {
                    resolve(1);
                } else {
                    const maxId = Math.max(...items.map(item => item[keyPath] || 0));
                    resolve(maxId + 1);
                }
            };

            getAllRequest.onerror = (event) => {
                reject(event.target.error);
            };
        };

        request.onerror = (event) => {
            reject(event.target.error);
        };
    });
};

const deleteRecordByField = async (storeName, target, id) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(_DBName, _Version);

        request.onsuccess = (event) => {
            const db = event.target.result;
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);

            const cursorRequest = store.openCursor();

            cursorRequest.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    const record = cursor.value;
                    if (record[target] === id) {
                        cursor.delete();
                    }
                    cursor.continue();
                } else {
                    resolve(`All matching records in '${storeName}' where '${target}' = ${id} deleted.`);
                }
            };

            cursorRequest.onerror = (event) => {
                reject(event.target.error);
            };
        };

        request.onerror = (event) => {
            reject(event.target.error);
        };
    });
};

const upsertRecordsBySessionId = async (storeName, sessionId, newData) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(_DBName, _Version);

        request.onsuccess = (event) => {
            const db = event.target.result;
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);
            const sessionRecords = [];

            // Step 1: Load all existing records with matching SessionID
            const cursorRequest = store.openCursor();

            cursorRequest.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    const value = cursor.value;
                    if (value.SessionID === sessionId) {
                        sessionRecords.push(value);
                    }
                    cursor.continue();
                } else {
                    // Step 2: Perform update or insert
                    let processed = 0;

                    if (newData.length === 0) return resolve("No data to upsert.");

                    newData.forEach((item) => {
                        const existing = sessionRecords.find(r => r.ID === item.ID);
                        if (existing) {
                            // Delete first
                            const deleteRequest = store.delete(item.ID);
                            deleteRequest.onsuccess = () => {
                                // Insert after successful delete
                                store.put(item);
                            };
                            deleteRequest.onerror = () => {
                                console.error(`Failed to delete record with ID: ${item.ID}`);
                            };
                        } else {
                            // Just insert
                            store.put(item);
                        }

                        processed++;
                        if (processed === newData.length) {
                            resolve(`Upserted ${processed} records for SessionID = ${sessionId}`);
                        }
                    });
                }
            };

            cursorRequest.onerror = (event) => {
                reject(`Cursor error: ${event.target.error}`);
            };
        };

        request.onerror = (event) => {
            reject(`Database open error: ${event.target.error}`);
        };
    });
};


const clearAllLocalData = async () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(_DBName, _Version);

        request.onsuccess = (event) => {
            const db = event.target.result;
            const transaction = db.transaction(db.objectStoreNames, 'readwrite');

            let clearedStores = [];
            let errorOccurred = false;

            for (const storeName of db.objectStoreNames) {
                if (storeName === 'Logs' || storeName === 'file' || storeName === 'Attendance') continue;

                const store = transaction.objectStore(storeName);
                const clearRequest = store.clear();

                clearRequest.onsuccess = () => {
                    clearedStores.push(storeName);
                };

                clearRequest.onerror = (e) => {
                    errorOccurred = true;
                    reject(`Failed to clear store "${storeName}": ${e.target.error}`);
                };
            }

            transaction.oncomplete = () => {
                if (!errorOccurred) {
                    resolve(`All stores cleared: ${clearedStores.join(', ')}`);
                }
            };

            transaction.onerror = (e) => {
                reject(`Transaction error: ${e.target.error}`);
            };
        };

        request.onerror = (event) => {
            reject(`Failed to open database: ${event.target.error}`);
        };
    });
};

const clearDataByStoreName = async (storeName) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(_DBName, _Version);

        request.onsuccess = (event) => {
            const db = event.target.result;

            if (!db.objectStoreNames.contains(storeName)) {
                reject(`Store "${storeName}" does not exist in the database.`);
                return;
            }

            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);
            const clearRequest = store.clear();

            clearRequest.onsuccess = () => {
                resolve(`Store "${storeName}" cleared successfully.`);
            };

            clearRequest.onerror = (e) => {
                reject(`Failed to clear store "${storeName}": ${e.target.error}`);
            };

            transaction.onerror = (e) => {
                reject(`Transaction error while clearing "${storeName}": ${e.target.error}`);
            };
        };

        request.onerror = (event) => {
            reject(`Failed to open database: ${event.target.error}`);
        };
    });
};


const deleteLocalDatabase = async () => {
    return new Promise((resolve, reject) => {
        const deleteRequest = indexedDB.deleteDatabase(_DBName);

        deleteRequest.onsuccess = () => {
            resolve(`Database "${_DBName}" deleted successfully.`);
        };

        deleteRequest.onerror = (event) => {
            reject(`Failed to delete database: ${event.target.error}`);
        };

        deleteRequest.onblocked = () => {
            reject("Database deletion is blocked. Please close all other tabs using this app.");
        };
    });
};

const checkIfDatabaseExists = async () => {
    if (!indexedDB.databases) {
        console.warn("indexedDB.databases() is not supported in this browser.");
        return false;
    }

    const dbs = await indexedDB.databases();
    return dbs.some(db => db.name === _DBName);
};

const markAsUploadedDone = async (storeName, records) => {
    if (!records.length) return;

    const db = await openDatabase();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, "readwrite");
        const store = tx.objectStore(storeName);

        // Detect keyPath automatically (string or array)
        const keyField = Array.isArray(store.keyPath) ? store.keyPath[0] : store.keyPath;

        records.forEach((record) => {
            const key = typeof record === "object" ? record[keyField] : record;
            if (key == null || key === "") return; // skip invalid keys

            const req = store.get(key);
            req.onsuccess = (event) => {
                const dbRecord = event.target.result;
                if (dbRecord) {
                    dbRecord.SyncStatus = SyncStatusMap.done; // or SyncStatusMap.done
                    store.put(dbRecord);
                }
            };
        });

        tx.oncomplete = () => resolve(true);
        tx.onerror = (err) => reject(err);
    });
};



export { addRecord, getAll, getRecordById, deleteRecord, updateRecord, getNextId, deleteRecordByField, syncDataToLocal, upsertRecordsBySessionId, clearAllLocalData, deleteLocalDatabase, openDatabase, checkIfDatabaseExists, clearDataByStoreName, markAsUploadedDone };
